import { FileText, FileSpreadsheet, Upload, X } from "lucide-react";
import React, { useRef, useState } from "react";

import { Flex } from "@/compat/radix-themes";

interface FileUploadProps {
  onFileChange?: (file: File | null) => void;
}

export const getFileIcon = (extension: string) => {
  switch (extension) {
    case "pdf":
      return <FileText className="w-3.5 h-3.5 text-zinc-500" />;
    case "doc":
    case "docx":
      return <FileText className="w-3.5 h-3.5 text-zinc-500" />;
    case "xls":
    case "xlsx":
      return <FileSpreadsheet className="w-3.5 h-3.5 text-zinc-500" />;
    case "csv":
      return <FileSpreadsheet className="w-3.5 h-3.5 text-zinc-500" />;
    case "txt":
      return <FileText className="w-3.5 h-3.5 text-zinc-400" />;
    default:
      return <FileText className="w-3.5 h-3.5 text-zinc-400" />;
  }
};

const FileUpload: React.FC<FileUploadProps> = ({ onFileChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string>("");

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "text/plain",
      "text/csv",
    ];

    if (!allowedTypes.includes(file.type)) {
      setUploadError(
        "Unsupported file type. Allowed: PDF, DOCX, XLSX, TXT, CSV",
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadError("File size exceeds 5MB limit");
      return;
    }

    setUploadError("");
    setSelectedFile(file);
    onFileChange?.(file);
  };

  const handleRemove = () => {
    setSelectedFile(null);
    setUploadError("");
    onFileChange?.(null);
  };

  return (
    <div className="space-y-2">
      {/* Upload box */}
      <Flex
        justify={"end"}
        align={"center"}
        className=" hover:border-primary/50 transition-colors cursor-pointer group"
      >
        {selectedFile ? (
          <div className="flex items-center gap-2 max-w-[260px] pl-2 pr-1 py-1 rounded-md border border-border bg-muted">
            <span className="shrink-0 text-muted-foreground">
              {getFileIcon(selectedFile.name.split(".").pop() || "")}
            </span>
            <span
              className="truncate text-[12px] font-medium text-foreground"
              title={selectedFile.name}
            >
              {selectedFile.name}
            </span>
            <button
              type="button"
              onClick={handleRemove}
              className="shrink-0 text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-200/60 rounded-md transition-colors cursor-pointer"
          >
            <Upload size={14} className="text-zinc-500" />
            Upload
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.txt,.csv,.xlsx"
          hidden
          onChange={handleFileUpload}
        />
      </Flex>

      {/* Error message */}
      {uploadError && <p className="text-sm text-red-500">{uploadError}</p>}
      {/* Selected file preview */}
    </div>
  );
};

export default FileUpload;
